import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';

import { ApiService } from './api.service';

export type TableStatus =
  | 'AVAILABLE'
  | 'OCCUPIED'
  | 'RESERVED'
  | 'BILL_REQUESTED'
  | 'AWAITING_PAYMENT'
  | 'UNAVAILABLE';

export type SessionStatus =
  | 'OPEN'
  | 'BILL_REQUESTED'
  | 'AWAITING_PAYMENT'
  | 'CLOSED'
  | 'CANCELED';

export type PaymentMethod =
  | 'CASH'
  | 'PIX'
  | 'DEBIT_CARD'
  | 'CREDIT_CARD'
  | 'ONLINE'
  | 'OTHER';

export interface TableSessionSummary {
  subtotal: number;
  serviceFeeRate: number;
  serviceFeeAmount: number;
  discountAmount: number;
  total: number;
  paidAmount: number;
  remainingAmount: number;
}

export interface TableOrderItem {
  id: number;
  quantity: number;
  unitPrice: number | string;
  total?: number | string;
  notes?: string | null;

  product?: {
    id: number;
    name: string;
  };
}

export interface TableOrder {
  id: number;
  status: string;
  source: string;
  subtotal: number | string;
  total: number | string;
  notes?: string | null;
  createdAt: string;
  items: TableOrderItem[];
}

export interface TablePayment {
  id: number;
  method: PaymentMethod;
  status: string;
  amount: number | string;
  notes?: string | null;
  transactionCode?: string | null;
  paidAt?: string | null;
  createdAt: string;
}

export interface TableSession {
  id: number;
  status: SessionStatus;
  customerName?: string | null;
  customerCount?: number | null;
  notes?: string | null;
  serviceFeeRate?: number | string;
  discountAmount?: number | string;
  openedAt: string;
  billRequestedAt?: string | null;
  orders?: TableOrder[];
  payments?: TablePayment[];
  summary?: TableSessionSummary;
}

export interface RestaurantTable {
  id: number;
  restaurantId: number;
  name: string;
  number: number | null;
  capacity: number | null;
  area?: string | null;
  status: TableStatus;
  isActive: boolean;
  publicToken?: string;
  activeSession?: TableSession | null;
}

export interface TablePayload {
  name: string;
  number?: number | null;
  capacity?: number | null;
  area?: string | null;
  isActive?: boolean;
}

// export interface TableQrCodeResponse {
//   tableId: number;
//   tableName: string;
//   publicToken: string;
//   publicUrl: string;
//   qrCodeDataUrl?: string;
// }


export interface TableQrCodeResponse {
  restaurant?: {
    id: number;
    name: string;
  };

  table?: {
    id: number;
    name: string;
    number: number | null;
    capacity: number | null;
    area?: string | null;
    status: TableStatus;
    isActive: boolean;
  };

  publicToken: string;
  publicUrl: string;
  qrCodeDataUrl: string;
}

export interface OpenSessionPayload {
  tableId: number;
  customerName?: string;
  customerCount?: number;
  notes?: string;
  serviceFeeRate?: number;
  discountAmount?: number;
}

export interface RegisterPaymentPayload {
  method: PaymentMethod;
  amount: number;
  notes?: string;
  transactionCode?: string;
}

export interface RestaurantSummary {
  id: number;
  name: string;
  slug?: string;
  isActive?: boolean;
}

@Injectable({
  providedIn: 'root'
})
export class TableManagementService {
  constructor(
    private readonly api: ApiService
  ) {}

  listTables(
    restaurantId: number
  ): Observable<RestaurantTable[]> {
    return this.api.get<RestaurantTable[]>(
      `/restaurants/${restaurantId}/tables`
    );
  }

  getTable(
    restaurantId: number,
    tableId: number
  ): Observable<RestaurantTable> {
    return this.api.get<RestaurantTable>(
      `/restaurants/${restaurantId}/tables/${tableId}`
    );
  }

  createTable(
    restaurantId: number,
    payload: TablePayload
  ): Observable<RestaurantTable> {
    return this.api.post<RestaurantTable>(
      `/restaurants/${restaurantId}/tables`,
      payload
    );
  }

  updateTable(
    restaurantId: number,
    tableId: number,
    payload: TablePayload
  ): Observable<RestaurantTable> {
    return this.api.put<RestaurantTable>(
      `/restaurants/${restaurantId}/tables/${tableId}`,
      payload
    );
  }

  deactivateTable(
    restaurantId: number,
    tableId: number
  ): Observable<any> {
    return this.api.delete(
      `/restaurants/${restaurantId}/tables/${tableId}`
    );
  }

  getQrCode(
    restaurantId: number,
    tableId: number
  ): Observable<TableQrCodeResponse> {
    return this.api.get<TableQrCodeResponse>(
      `/restaurants/${restaurantId}/tables/${tableId}/qrcode`
    );
  }

  regenerateQrCode(
    restaurantId: number,
    tableId: number
  ): Observable<TableQrCodeResponse> {
    return this.api.post<TableQrCodeResponse>(
      `/restaurants/${restaurantId}/tables/${tableId}/qrcode/regenerate`,
      {}
    );
  }

  openSession(
    restaurantId: number,
    payload: OpenSessionPayload
  ): Observable<any> {
    return this.api.post(
       `/restaurants/${restaurantId}/tables/${payload.tableId}/sessions`,
    payload
    );
  }

  getSession(
    restaurantId: number,
    sessionId: number
  ): Observable<TableSession> {
    return this.api.get<TableSession>(
      `/restaurants/${restaurantId}/table-sessions/${sessionId}`
    );
  }

  requestBill(
    restaurantId: number,
    sessionId: number
  ): Observable<any> {
    return this.api.post(
      `/restaurants/${restaurantId}/table-sessions/${sessionId}/request-bill`,
      {}
    );
  }

  registerPayment(
    restaurantId: number,
    sessionId: number,
    payload: RegisterPaymentPayload
  ): Observable<any> {
    return this.api.post(
      `/restaurants/${restaurantId}/table-sessions/${sessionId}/payments`,
      payload
    );
  }

  cancelPayment(
    restaurantId: number,
    sessionId: number,
    paymentId: number
  ): Observable<any> {
    return this.api.post(
      `/restaurants/${restaurantId}/table-sessions/${sessionId}/payments/${paymentId}/cancel`,
      {}
    );
  }






transferSession(
  restaurantId: number,
  sessionId: number,
  targetTableId: number
) {
  return this.api.post<{
    message: string;
    session: TableSession;
  }>(
    `/restaurants/${restaurantId}` +
    `/table-sessions/${sessionId}` +
    `/transfer`,
    {
      targetTableId
    }
  );
}



  


  listRestaurants(): Observable<RestaurantSummary[]> {
  return this.api.get<RestaurantSummary[]>(
    '/restaurants'
  );
}

  closeSession(
    restaurantId: number,
    sessionId: number
  ): Observable<any> {
    return this.api.post(
      `/restaurants/${restaurantId}/table-sessions/${sessionId}/close`,
      {}
    );
  }
}